import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { ReactNode, useCallback, useRef, useState } from "react";
import { useTenantTranslation } from "hooks/formatters";
import {
  FloatingPanel,
  FloatingPanelPlacement,
  FloatingPanelSize,
} from "./FloatingPanel";

type PopoutContainerProps = Readonly<{
  children: ReactNode;
  title: string;
  poppedOutMessage?: string;
  popoutWidth?: number;
}>;

const DEFAULT_POPOUT_HEIGHT = 400;
const POPOUT_POSITION = { x: 100, y: 100 };

export const PopoutContainer = ({
  children,
  title,
  poppedOutMessage,
  popoutWidth = 350,
}: PopoutContainerProps) => {
  const { t } = useTenantTranslation();
  const [poppedOutSize, setPoppedOutSize] = useState<FloatingPanelSize | null>(
    null
  );
  const [lastPlacement, setLastPlacement] =
    useState<FloatingPanelPlacement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isReturningFocusRef = useRef(false);

  const popOutButtonRef = useCallback((button: HTMLButtonElement | null) => {
    if (button && isReturningFocusRef.current) {
      isReturningFocusRef.current = false;
      button.focus();
    }
  }, []);

  const handlePopOut = () => {
    const rect = containerRef.current?.getBoundingClientRect();
    setPoppedOutSize(
      rect
        ? { width: rect.width, height: rect.height }
        : { width: popoutWidth, height: DEFAULT_POPOUT_HEIGHT }
    );
  };

  if (poppedOutSize === null) {
    return (
      <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
        <Tooltip title={t("popOut")}>
          <IconButton
            ref={popOutButtonRef}
            size="small"
            onClick={handlePopOut}
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              zIndex: 1,
              bgcolor: "background.paper",
              boxShadow: 1,
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box ref={containerRef} sx={{ width: "100%" }}>
          {children}
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {poppedOutMessage ?? t("layoutPoppedOut")}
        </Typography>
      </Box>
      <FloatingPanel
        title={title}
        initialPosition={lastPlacement?.position ?? POPOUT_POSITION}
        initialSize={poppedOutSize}
        initialOpacity={lastPlacement?.opacity}
        onClose={(placement) => {
          setLastPlacement(placement);
          isReturningFocusRef.current = true;
          setPoppedOutSize(null);
        }}
      >
        {children}
      </FloatingPanel>
    </>
  );
};
